import { useEffect, useMemo } from 'react'
import dayjs from 'dayjs'
import type { Alpha3Code } from 'i18n-iso-countries'

import { getAccountGivingActions } from '@percent/workplace-giving/api/account/givingActions/getAccountGivingActions/getAccountGivingActions'
import type { AccountGivingActionRow } from '@percent/workplace-giving/api/account/givingActions/getAccountGivingActions/getAccountGivingActions.types'
import { useAuth, useInfiniteQuery, useLogger, useWindowDimensions } from '@percent/workplace-giving/common/hooks'
import { getCountryCodeFromAuthState } from '@percent/workplace-giving/context/auth/authContextController/AuthContextController'

export type UseGivingActionsRow = Readonly<{
  givingActions: AccountGivingActionRow[]
  skeletonData: string[]
  tilesAmount: number
  isPending: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  fetchNextPage: () => void
}>

const SCREEN_WIDTH_BREAKPOINT = 1280
const DEFAULT_PAGE_SIZE = 25

export type UseGivingActionsRowProps = Readonly<{
  campaignId?: string
  tilesAmount?: number
  targetCountries?: Alpha3Code[]
  includeUnpublished?: boolean
}>

export const useGivingActionsRow = ({
  campaignId,
  tilesAmount: tilesAmountOverride,
  targetCountries,
  includeUnpublished
}: UseGivingActionsRowProps = {}): UseGivingActionsRow => {
  const { state } = useAuth()
  const { logError } = useLogger()
  const { width: windowWidth } = useWindowDimensions()

  const country = useMemo(() => {
    if (!targetCountries) {
      return getCountryCodeFromAuthState(state)
    }

    return targetCountries.length === 1 ? targetCountries[0] : null
  }, [targetCountries, state])
  const tilesAmount = tilesAmountOverride ?? (windowWidth > SCREEN_WIDTH_BREAKPOINT ? 4 : 3)

  const { data, isPending, isFetchingNextPage, hasNextPage, fetchNextPage, isError, error } = useInfiniteQuery(
    [
      'getAccountGivingActions',
      {
        date: dayjs().format('YYYY-MM-DD'),
        country,
        pageSize: DEFAULT_PAGE_SIZE,
        direction: 'DESC' as const,
        ...(campaignId ? { campaignId } : {}),
        ...(includeUnpublished ? { includeUnpublished } : {})
      }
    ],
    getAccountGivingActions,
    { staleTime: 1000 * 20 }
  )

  useEffect(() => {
    if (isError && error) {
      logError(error)
    }
  }, [isError, error, logError])

  return {
    givingActions: data?.pages.flatMap(page => page.data) ?? [],
    skeletonData: Array.from({ length: tilesAmount }, (_, i) => i.toString()),
    tilesAmount,
    isPending,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage
  }
}
